/**
 * Store Locator Web Component
 *
 * Interactive map with store locations using Leaflet.js (OpenStreetMap)
 * Features:
 * - Interactive map with custom markers
 * - Geolocation support with distance calculation
 * - Search/filter by city, postal code
 * - Filter by sortiment category
 * - Synced map/list interaction
 */

class StoreLocator extends HTMLElement {
  constructor() {
    super();

    this.map = null;
    this.markers = [];
    this.userMarker = null;
    this.userLocation = null;
    this.stores = [];
    this.filteredStores = [];
    this.translations = {};
    this.activeFilter = 'all';
    this.searchQuery = '';
    this.activeStoreId = null;
    this.locationIsApproximate = false;

    // Handle of the store that can be pinned to the top of the list
    this.factoryHandle = 'chocolate_factory_store';
    // Pin the Chocolate Factory Store as #1. Set to false during a temporary
    // closure. Note it does TWO things: it pins the store to the top, and it
    // exempts the store from the category filter in applyFilters() below — so
    // turning it off also makes the store disappear whenever a visitor filters
    // by a sortiment category the store doesn't match.
    this.pinFactory = true;

    // Bind methods
    this.handleFilterClick = this.handleFilterClick.bind(this);
    this.handleSearchInput = this.handleSearchInput.bind(this);
    this.handleStoreCardClick = this.handleStoreCardClick.bind(this);
    this.handleGeolocation = this.handleGeolocation.bind(this);
  }

  connectedCallback() {
    // Load data from script tags
    this.loadData();

    // Wait for Leaflet to be available
    this.initWhenReady();
  }

  loadData() {
    const storeDataEl = this.querySelector('[data-store-data]');
    const translationsEl = this.querySelector('[data-translations]');

    if (storeDataEl) {
      try {
        this.stores = JSON.parse(storeDataEl.textContent);
        this.filteredStores = [...this.stores];
      } catch (e) {
        console.error('Failed to parse store data:', e);
      }
    }

    if (translationsEl) {
      try {
        this.translations = JSON.parse(translationsEl.textContent);
      } catch (e) {
        console.error('Failed to parse translations:', e);
      }
    }
  }

  initWhenReady() {
    // Check if Leaflet is loaded
    if (typeof L !== 'undefined') {
      this.init();
    } else {
      // Wait for Leaflet to load
      const checkInterval = setInterval(() => {
        if (typeof L !== 'undefined') {
          clearInterval(checkInterval);
          this.init();
        }
      }, 100);

      // Timeout after 10 seconds
      setTimeout(() => clearInterval(checkInterval), 10000);
    }
  }

  init() {
    this.initMap();
    this.initMarkers();
    this.bindEvents();
    this.renderOpeningHours();

    // Fit map to show all stores, then try auto geolocation
    this.fitMapToStores();

    // Apply ordering immediately so the Chocolate Factory Store is pinned first,
    // even before we know the visitor's location.
    this.applyFilters();

    // No auto-prompt. Reuse a fresh cached location silently if the visitor
    // already shared it this session; otherwise wait for the "Use my location"
    // button.
    this.tryAutoGeolocation();
  }

  /**
   * Fit map bounds to show all stores
   */
  fitMapToStores() {
    if (!this.map || this.markers.length === 0) return;

    const group = L.featureGroup(this.markers);
    // Use maxZoom to prevent zooming in too close (especially with single store)
    this.map.fitBounds(group.getBounds().pad(0.3), { maxZoom: 12 });
  }

  initMap() {
    const mapContainer = this.querySelector('[data-map-container]');
    if (!mapContainer) return;

    // Default center on Denmark
    const defaultLat = parseFloat(this.dataset.defaultLat) || 55.9;
    const defaultLng = parseFloat(this.dataset.defaultLng) || 10.5;
    const defaultZoom = parseInt(this.dataset.defaultZoom) || 7;

    // Initialize Leaflet map
    this.map = L.map(mapContainer, {
      center: [defaultLat, defaultLng],
      zoom: defaultZoom,
      scrollWheelZoom: true,
      zoomControl: true
    });

    // CartoDB Positron tiles — minimalistic light gray base, free for commercial use.
    // CSS filter in component-store-locator.css adds a subtle warm sepia tint
    // to match Simply's cream (#fbfaf7) aesthetic.
    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
      subdomains: 'abcd',
      maxZoom: 19
    }).addTo(this.map);

    // Position zoom control
    this.map.zoomControl.setPosition('topright');
  }

  /**
   * Try to use cached geolocation from session storage
   */
  tryAutoGeolocation() {
    try {
      const cached = sessionStorage.getItem('store-locator-location');
      if (cached) {
        const { lat, lng, timestamp } = JSON.parse(cached);
        // Use cache if less than 10 minutes old
        if (Date.now() - timestamp < 600000) {
          this.userLocation = { lat, lng };
          this.locationIsApproximate = false;
          this.addUserMarker();
          this.applyFilters();

          // Zoom to show user and nearest stores
          if (this.filteredStores.length > 0) {
            const nearestStore = this.getNearestStore() || this.filteredStores[0];
            const bounds = L.latLngBounds(
              [this.userLocation.lat, this.userLocation.lng],
              [nearestStore.lat, nearestStore.lng]
            );
            this.map.fitBounds(bounds.pad(0.3));
          }
        }
      }
    } catch (e) {
      // Ignore cache errors
    }
  }

  /**
   * Cache user location to session storage
   */
  cacheUserLocation() {
    if (this.userLocation) {
      try {
        sessionStorage.setItem('store-locator-location', JSON.stringify({
          lat: this.userLocation.lat,
          lng: this.userLocation.lng,
          timestamp: Date.now()
        }));
      } catch (e) {
        // Ignore storage errors
      }
    }
  }

  /**
   * Sort stores by distance from user
   */
  sortByDistance() {
    if (!this.userLocation) return;

    this.filteredStores.sort((a, b) => {
      const distA = this.calculateDistance(
        this.userLocation.lat,
        this.userLocation.lng,
        a.lat,
        a.lng
      );
      const distB = this.calculateDistance(
        this.userLocation.lat,
        this.userLocation.lng,
        b.lat,
        b.lng
      );
      return distA - distB;
    });
  }

  /**
   * Identify the Chocolate Factory Store, which is always pinned first.
   * Matches on handle, with a name fallback in case the handle changes.
   */
  isFactoryStore(store) {
    if (!store) return false;
    if (store.handle && store.handle === this.factoryHandle) return true;
    return (store.name || '').toLowerCase().includes('chocolate factory');
  }

  /**
   * Return the genuinely closest store to the visitor (ignores the Factory pin),
   * used only for framing the map — not for list ordering.
   */
  getNearestStore() {
    if (!this.userLocation || this.filteredStores.length === 0) return null;
    let nearest = null;
    let best = Infinity;
    this.filteredStores.forEach(store => {
      const lat = parseFloat(store.lat);
      const lng = parseFloat(store.lng);
      if (!isFinite(lat) || !isFinite(lng)) return;
      const dist = this.calculateDistance(this.userLocation.lat, this.userLocation.lng, lat, lng);
      if (dist < best) {
        best = dist;
        nearest = store;
      }
    });
    return nearest;
  }

  /**
   * Move the Chocolate Factory Store to the top of the filtered list,
   * regardless of distance, so it is always #1.
   */
  pinFactoryFirst() {
    const idx = this.filteredStores.findIndex(s => this.isFactoryStore(s));
    if (idx > 0) {
      const [factory] = this.filteredStores.splice(idx, 1);
      this.filteredStores.unshift(factory);
    }
  }

  /**
   * Reorder the visible store-card list items to match filteredStores order
   * (Chocolate Factory first, then nearest-first when a location is known).
   */
  reorderStoreList() {
    const orderedIds = this.filteredStores.map(s => String(s.id));
    const lists = this.querySelectorAll('[data-store-list]');

    lists.forEach(list => {
      const items = Array.from(list.querySelectorAll(':scope > li'));
      const byId = new Map();
      items.forEach(li => {
        const card = li.querySelector('.store-card');
        if (card) byId.set(String(card.dataset.storeId), li);
      });

      // Re-append matched items in the desired order.
      orderedIds.forEach(id => {
        const li = byId.get(id);
        if (li) list.appendChild(li);
      });

      // Push any remaining (hidden/non-matching) items to the end.
      items.forEach(li => {
        const card = li.querySelector('.store-card');
        const id = card ? String(card.dataset.storeId) : null;
        if (!id || !orderedIds.includes(id)) list.appendChild(li);
      });
    });
  }

  initMarkers() {
    if (!this.map) return;

    this.stores.forEach(store => {
      if (!store.lat || !store.lng) return;

      // Simple premium pin marker — dark drop shape with gold dot
      const markerClass = this.getMarkerClass(store.sortimentCategory);
      const icon = L.divIcon({
        className: `store-locator-marker ${markerClass}`,
        html: `
          <svg class="store-locator-marker__svg" viewBox="0 0 36 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M18 1 C9 1 2 8 2 17 C2 28 10 40 18 47 C26 40 34 28 34 17 C34 8 27 1 18 1 Z" class="store-locator-marker__pin"/>
            <circle cx="18" cy="17" r="5.5" class="store-locator-marker__dot"/>
          </svg>
        `,
        iconSize: [36, 48],
        iconAnchor: [18, 46],
        popupAnchor: [0, -40]
      });

      const marker = L.marker([store.lat, store.lng], { icon })
        .addTo(this.map);

      // Create popup content
      const popupContent = this.createPopupContent(store);
      marker.bindPopup(popupContent, {
        maxWidth: 280,
        className: 'store-locator-popup'
      });

      // Store reference
      marker.storeId = store.id;
      this.markers.push(marker);

      // Handle marker click
      marker.on('click', () => {
        this.setActiveStore(store.id);
      });
    });
  }

  getMarkerClass(sortimentCategory) {
    switch (sortimentCategory) {
      case 'full_sortiment':
        return 'store-locator-marker--full';
      case 'stoerstedelen':
        return 'store-locator-marker--most';
      case 'mindre_udvalg':
        return 'store-locator-marker--limited';
      default:
        return 'store-locator-marker--full';
    }
  }

  createPopupContent(store) {
    const appleMapsUrl = `https://maps.apple.com/?daddr=${store.lat},${store.lng}`;
    const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${store.lat},${store.lng}`;

    return `
      <div class="store-popup">
        <h4 class="store-popup__name">${this.escapeHtml(store.name)}</h4>
        <p class="store-popup__address">
          ${this.escapeHtml(store.address)}<br>
          ${this.escapeHtml(store.postalCode)} ${this.escapeHtml(store.city)}
        </p>
        <div class="store-popup__links">
          <a href="${appleMapsUrl}" target="_blank" rel="noopener" class="store-popup__link store-popup__link--apple">Apple Maps</a>
          <span class="store-popup__links-separator">·</span>
          <a href="${googleMapsUrl}" target="_blank" rel="noopener" class="store-popup__link store-popup__link--google">Google Maps</a>
        </div>
      </div>
    `;
  }

  bindEvents() {
    // Filter buttons
    const filterBtns = this.querySelectorAll('[data-filter]');
    filterBtns.forEach(btn => {
      btn.addEventListener('click', this.handleFilterClick);
    });

    // Search input
    const searchInput = this.querySelector('[data-search-input]');
    if (searchInput) {
      searchInput.addEventListener('input', this.debounce(this.handleSearchInput, 300));
    }

    // Store cards
    const storeCards = this.querySelectorAll('.store-card');
    storeCards.forEach(card => {
      card.addEventListener('click', this.handleStoreCardClick);
    });

    // Geolocation button
    const geoBtn = this.querySelector('[data-geo-btn]');
    if (geoBtn) {
      geoBtn.addEventListener('click', this.handleGeolocation);
    }

    // Opening hours toggles
    const hoursToggles = this.querySelectorAll('[data-hours-toggle]');
    hoursToggles.forEach(toggle => {
      toggle.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleOpeningHours(toggle);
      });
    });
  }

  handleFilterClick(e) {
    const filter = e.currentTarget.dataset.filter;

    // Update active state on ALL filter buttons (both mobile and desktop)
    const filterBtns = this.querySelectorAll('[data-filter]');
    filterBtns.forEach(btn => {
      const isActive = btn.dataset.filter === filter;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-pressed', isActive);
    });

    this.activeFilter = filter;

    // Reset filteredStores to all stores before applying new filter
    this.filteredStores = [...this.stores];
    this.applyFilters();
  }

  handleSearchInput(e) {
    this.searchQuery = e.target.value.toLowerCase().trim();
    this.applyFilters();
  }

  applyFilters() {
    // Filter stores
    this.filteredStores = this.stores.filter(store => {
      // Category filter — when pinned, the Chocolate Factory Store bypasses
      // this and is always shown, regardless of the active category filter.
      if (
        this.activeFilter !== 'all' &&
        store.sortimentCategory !== this.activeFilter &&
        !(this.pinFactory && this.isFactoryStore(store))
      ) {
        return false;
      }

      // Search filter (applies to every store, including the Factory store —
      // when the visitor is actively searching, only matches are shown)
      if (this.searchQuery) {
        const searchFields = [
          store.name,
          store.city,
          store.postalCode,
          store.address
        ].map(f => (f || '').toLowerCase());

        return searchFields.some(field => field.includes(this.searchQuery));
      }

      return true;
    });

    // Sort by distance if user location is available (precise or IP-based)
    if (this.userLocation) {
      this.filteredStores.sort((a, b) => {
        const distA = this.calculateDistance(
          this.userLocation.lat,
          this.userLocation.lng,
          a.lat,
          a.lng
        );
        const distB = this.calculateDistance(
          this.userLocation.lat,
          this.userLocation.lng,
          b.lat,
          b.lng
        );
        return distA - distB;
      });
    }

    // The Chocolate Factory Store is #1 when pinning is enabled.
    if (this.pinFactory) {
      this.pinFactoryFirst();
    }

    this.updateUI();
  }

  updateUI() {
    // Update markers visibility
    this.markers.forEach(marker => {
      const store = this.filteredStores.find(s => String(s.id) === String(marker.storeId));
      if (store) {
        marker.setOpacity(1);
        marker.options.interactive = true;
      } else {
        marker.setOpacity(0.2);
        marker.options.interactive = false;
      }
    });

    // Update store list
    const storeCards = this.querySelectorAll('.store-card');
    const filteredIds = this.filteredStores.map(s => String(s.id));

    storeCards.forEach(card => {
      const storeId = String(card.dataset.storeId);
      const isVisible = filteredIds.includes(storeId);
      const listItem = card.closest('li');
      if (listItem) listItem.hidden = !isVisible;

      // Update distance if available
      if (this.userLocation) {
        const store = this.stores.find(s => String(s.id) === storeId);
        if (store) {
          const distance = this.calculateDistance(
            this.userLocation.lat,
            this.userLocation.lng,
            store.lat,
            store.lng
          );
          this.updateCardDistance(card, distance);
        }
      }
    });

    // Reorder the list so it matches filteredStores (Factory first, nearest next)
    this.reorderStoreList();

    // Update store count (both desktop and mobile)
    const countEls = this.querySelectorAll('[data-store-count]');
    const count = this.filteredStores.length;
    countEls.forEach(el => {
      el.textContent = `${count} ${count === 1 ? 'butik' : 'butikker'}`;
    });

    // Show/hide empty states (both desktop and mobile)
    const emptyStates = this.querySelectorAll('[data-empty-state]');
    const emptyFilterStates = this.querySelectorAll('[data-empty-filter-state]');
    const storeLists = this.querySelectorAll('[data-store-list]');

    if (this.filteredStores.length === 0) {
      storeLists.forEach(el => el.hidden = true);
      emptyFilterStates.forEach(el => el.hidden = false);
    } else {
      storeLists.forEach(el => el.hidden = false);
      emptyFilterStates.forEach(el => el.hidden = true);
    }

    // Fit map to visible markers
    if (this.filteredStores.length > 0 && this.map) {
      const visibleMarkers = this.markers.filter(m =>
        this.filteredStores.some(s => String(s.id) === String(m.storeId))
      );
      if (visibleMarkers.length > 0) {
        const group = L.featureGroup(visibleMarkers);
        this.map.fitBounds(group.getBounds().pad(0.1));
      }
    }
  }

  updateCardDistance(card, distance) {
    let distanceEl = card.querySelector('.store-card__distance');

    if (!distanceEl) {
      // Create distance element if it doesn't exist
      distanceEl = document.createElement('div');
      distanceEl.className = 'store-card__distance';
      distanceEl.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="10" r="3"/>
          <path d="M12 21.7C17.3 17 20 13 20 10a8 8 0 1 0-16 0c0 3 2.7 7 8 11.7z"/>
        </svg>
        <span></span>
      `;

      const addressEl = card.querySelector('.store-card__address');
      if (addressEl) {
        addressEl.after(distanceEl);
      }
    }

    const span = distanceEl.querySelector('span') || distanceEl;
    if (distance < 1) {
      span.textContent = this.translations.distanceM?.replace('__DISTANCE__', Math.round(distance * 1000)) || `${Math.round(distance * 1000)} m væk`;
    } else {
      span.textContent = this.translations.distanceKm?.replace('__DISTANCE__', distance.toFixed(1)) || `${distance.toFixed(1)} km væk`;
    }
  }

  handleStoreCardClick(e) {
    // Don't trigger if clicking on a link or button
    if (e.target.closest('a, button')) return;

    const card = e.currentTarget;
    const storeId = card.dataset.storeId;
    this.setActiveStore(storeId);
  }

  setActiveStore(storeId) {
    this.activeStoreId = String(storeId);

    // Update card states
    const cards = this.querySelectorAll('.store-card');
    cards.forEach(card => {
      card.classList.toggle('is-active', String(card.dataset.storeId) === this.activeStoreId);
    });

    // Update marker states and pan to marker
    const marker = this.markers.find(m => String(m.storeId) === this.activeStoreId);
    if (marker && this.map) {
      // Update marker classes
      this.markers.forEach(m => {
        const el = m.getElement();
        if (el) {
          el.classList.toggle('is-active', String(m.storeId) === this.activeStoreId);
        }
      });

      // Pan to marker and open popup
      this.map.setView(marker.getLatLng(), Math.max(this.map.getZoom(), 13), {
        animate: true,
        duration: 0.5
      });
      marker.openPopup();
    }

    // Scroll card into view on mobile
    const activeCard = this.querySelector(`.store-card[data-store-id="${storeId}"]`);
    if (activeCard) {
      activeCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  handleGeolocation() {
    const btn = this.querySelector('[data-geo-btn]');
    const btnText = this.querySelector('[data-geo-btn-text]');

    if (!navigator.geolocation) {
      alert(this.translations.error || 'Geolocation is not supported');
      return;
    }

    // Show loading state
    btn.classList.add('is-loading');
    if (btnText) btnText.textContent = this.translations.finding || 'Finding your location...';

    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.userLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        this.locationIsApproximate = false;

        // Cache location for future visits
        this.cacheUserLocation();

        // Add user marker
        this.addUserMarker();

        // Sort and update
        this.applyFilters();

        // Reset button
        btn.classList.remove('is-loading');
        if (btnText) btnText.textContent = this.translations.useLocation || 'Brug min placering';

        // Pan map to show user and nearest store
        if (this.filteredStores.length > 0) {
          const nearestStore = this.getNearestStore() || this.filteredStores[0];
          const bounds = L.latLngBounds(
            [this.userLocation.lat, this.userLocation.lng],
            [nearestStore.lat, nearestStore.lng]
          );
          this.map.fitBounds(bounds.pad(0.2));
        }
      },
      (error) => {
        btn.classList.remove('is-loading');
        if (btnText) btnText.textContent = this.translations.useLocation || 'Use my location';

        let message = this.translations.error || 'An error occurred';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            message = this.translations.permissionDenied || 'Location access was denied';
            break;
          case error.POSITION_UNAVAILABLE:
            message = this.translations.positionUnavailable || 'Location unavailable';
            break;
          case error.TIMEOUT:
            message = this.translations.timeout || 'Request timed out';
            break;
        }
        alert(message);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes
      }
    );
  }

  addUserMarker() {
    if (!this.map || !this.userLocation) return;

    // Remove existing user marker
    if (this.userMarker) {
      this.map.removeLayer(this.userMarker);
    }

    // Create user location marker
    const icon = L.divIcon({
      className: 'store-locator-user-marker',
      iconSize: [16, 16],
      iconAnchor: [8, 8]
    });

    this.userMarker = L.marker([this.userLocation.lat, this.userLocation.lng], { icon })
      .addTo(this.map);
  }

  /**
   * Haversine formula to calculate distance between two points
   * Returns distance in kilometers
   */
  calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371; // Earth's radius in km
    const dLat = this.toRad(lat2 - lat1);
    const dLon = this.toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  toRad(deg) {
    return deg * (Math.PI / 180);
  }

  toggleOpeningHours(toggle) {
    const expanded = toggle.getAttribute('aria-expanded') === 'true';
    const hoursContainer = toggle.nextElementSibling;

    toggle.setAttribute('aria-expanded', !expanded);
    hoursContainer.hidden = expanded;

    // Update button text
    const textEl = toggle.querySelector('[data-toggle-text]');
    if (textEl) {
      textEl.textContent = expanded
        ? (this.translations.showHours || 'Vis åbningstider')
        : (this.translations.hideHours || 'Skjul åbningstider');
    }
  }

  renderOpeningHours() {
    const hoursContainers = this.querySelectorAll('.store-card__hours[data-opening-hours]');

    hoursContainers.forEach(container => {
      try {
        let hoursData = container.dataset.openingHours;
        if (!hoursData || hoursData === 'null') return;

        // Parse JSON - may need to parse twice if double-encoded
        let hours = JSON.parse(hoursData);
        if (typeof hours === 'string') {
          hours = JSON.parse(hours);
        }

        if (!hours || typeof hours !== 'object') return;

        // Support both Danish and English day names
        const dayMapping = [
          { en: 'monday', da: 'Mandag', jsDay: 1 },
          { en: 'tuesday', da: 'Tirsdag', jsDay: 2 },
          { en: 'wednesday', da: 'Onsdag', jsDay: 3 },
          { en: 'thursday', da: 'Torsdag', jsDay: 4 },
          { en: 'friday', da: 'Fredag', jsDay: 5 },
          { en: 'saturday', da: 'Lørdag', jsDay: 6 },
          { en: 'sunday', da: 'Søndag', jsDay: 0 }
        ];

        // Check if currently open
        const isOpen = this.isStoreOpen(hours, dayMapping);

        // Update status badge
        const section = container.closest('.store-card__hours-section');
        if (section) {
          const badge = section.querySelector('[data-status-badge]');
          if (badge) {
            badge.textContent = isOpen ? 'Åben' : 'Lukket';
            badge.className = `store-card__status-badge store-card__status-badge--${isOpen ? 'open' : 'closed'}`;
          }
        }

        // Render hours list
        let html = '<div class="store-card__hours-list">';
        dayMapping.forEach(({ en, da }) => {
          // Try Danish name first, then English
          const dayData = hours[da] || hours[en];
          const dayLabel = this.translations.days?.[en] || da;

          if (dayData && dayData.open && dayData.close) {
            html += `
              <span class="store-card__hours-day">${dayLabel}</span>
              <span class="store-card__hours-time">${dayData.open} - ${dayData.close}</span>
            `;
          } else {
            // Day is closed
            html += `
              <span class="store-card__hours-day">${dayLabel}</span>
              <span class="store-card__hours-time store-card__hours-time--closed">${this.translations.closed || 'Lukket'}</span>
            `;
          }
        });
        html += '</div>';

        container.innerHTML = html;
      } catch (e) {
        console.error('Failed to parse opening hours:', e);
      }
    });
  }

  /**
   * Check if store is currently open based on opening hours
   */
  isStoreOpen(hours, dayMapping) {
    const now = new Date();
    const currentDay = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const currentTime = now.getHours() * 100 + now.getMinutes(); // e.g., 1430 for 14:30

    // Find today's day mapping
    const todayMapping = dayMapping.find(d => d.jsDay === currentDay);
    if (!todayMapping) return false;

    // Get today's hours (try Danish first, then English)
    const todayHours = hours[todayMapping.da] || hours[todayMapping.en];

    if (!todayHours || !todayHours.open || !todayHours.close) {
      return false; // Closed today
    }

    // Parse opening and closing times (format: "10:00")
    const [openHour, openMin] = todayHours.open.split(':').map(Number);
    const [closeHour, closeMin] = todayHours.close.split(':').map(Number);

    const openTime = openHour * 100 + openMin;
    const closeTime = closeHour * 100 + closeMin;

    return currentTime >= openTime && currentTime < closeTime;
  }

  escapeHtml(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func.apply(this, args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Register custom element
if (!customElements.get('store-locator')) {
  customElements.define('store-locator', StoreLocator);
}
